import { Box, Skeleton, Tab } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloseDialogButton, DialogContentWrapper, DialogTitle } from '../../../shared/dialog/dialog.styles';
import { randomBetween } from '../patient-profile-widget/utils/random-between';
import {
  DialogContent,
  MessageContainer,
  MessageContent,
  PatientInfoText,
  SenderLabel,
  StyledTabs
} from './mila.styles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface MilaModalSkeletonProps {
  onClose: () => void;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`mila-tabpanel-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const MilaModalSkeleton: FC<MilaModalSkeletonProps> = ({ onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const scrollToTop = useCallback(() => {
    const el = scrollContainerRef.current;

    if (el) {
      el.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Memoize random values so skeleton layout remains stable across renders
  // For messages, store an array of line objects per message (1-3 lines)
  const messageSkeletons = useMemo(
    () =>
      Array.from({ length: 6 }, () => {
        const widths = [randomBetween(200, 400)];

        if (Math.random() < 0.7) {
          widths.push(randomBetween(150, 350));
        }

        if (Math.random() < 0.4) {
          widths.push(randomBetween(100, 300));
        }

        return widths.map((w) => ({ id: Math.random().toString(36).slice(2, 10), width: w }));
      }),
    []
  );

  const detailSkeletons = useMemo(
    () => Array.from({ length: 4 }, () => ({ id: Math.random().toString(36).slice(2, 10), width: randomBetween(200, 350) })),
    []
  );

  const messageItem = (
    index: number,
    isAgent = false,
    lines: { id: string; width: number }[]
  ) => {
    const skeletonColorSx = !isAgent ? { backgroundColor: 'rgba(255, 255, 255, 0.11)' } : {};

    return (
      <MessageContainer key={`message-skeleton-${index}`}>
        {isAgent && <SenderLabel>Agent</SenderLabel>}
        <MessageContent isAgent={isAgent}>
          {lines.map((line, i) => (
            <Skeleton
              key={line.id}
              variant="text"
              width={line.width}
              height={20}
              sx={{ ...(i > 0 ? { mt: 1 } : {}), ...skeletonColorSx }}
            />
          ))}
          <Skeleton variant="text" width={80} height={14} sx={{ mt: 1, opacity: 0.7, ...skeletonColorSx }} />
        </MessageContent>
      </MessageContainer>
    );
  };

  const detailItem = (index: number, item: { id: string; width: number }) => (
    <Box key={item.id} sx={{ mb: 1 }}>
      <Skeleton variant="text" width={item.width} height={24} />
    </Box>
  );

  return (
    <DialogContentWrapper>
      <DialogTitle sx={{ cursor: 'default' }}>
        <Skeleton variant="text" width={300} height={28} />
        <PatientInfoText>
          <Skeleton variant="text" width={200} height={20} />
        </PatientInfoText>
        <CloseDialogButton onClick={onClose} />
      </DialogTitle>

      <StyledTabs value={tabValue} onChange={handleTabChange} textColor="inherit">
        <Tab label="TRANSCRIPT" />
        <Tab label="ADDITIONAL INFO" />
      </StyledTabs>

      <DialogContent ref={scrollContainerRef}>
        <TabPanel value={tabValue} index={0}>
          <div>{messageSkeletons.map((lines, index) => messageItem(index, index % 2 === 0, lines))}</div>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <div style={{ position: 'relative' }}>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
            <Box sx={{ mb: 3 }}>{detailSkeletons.map((item, index) => detailItem(index, item))}</Box>
          </div>
        </TabPanel>
      </DialogContent>
    </DialogContentWrapper>
  );
};
